export type TeamSide = 'A' | 'B';
export type CardKind = 'yellow' | 'red' | 'blue';
export type TimeoutKind = 'none' | 'ref' | 'A' | 'B';
export type PersonKind = 'player' | 'official';
export type DisplayLang = 'zh' | 'en';
export type BreakBeep = 'off' | 'min3' | 'custom';

export interface ScoreboardPeriod {
  id: string;
  name: string;
  durationSec: number;
  goalsA: number;
  halfA: number;
  goalsB: number;
  halfB: number;
  timeoutsUsedA: number;
  timeoutsUsedB: number;
}

export interface ScoreboardPlayer {
  id: string;
  number: string;
  name: string;
  goals: number;
  half: number;
  /** 2-minute penalties recorded for this player. */
  twoMin: number;
}

export interface ScoreboardOfficial {
  id: string;
  number: string;
  name: string;
}

export interface PenaltyCard {
  id: string;
  team: TeamSide;
  kind: CardKind;
  playerNo: string;
  playerName: string;
  who: PersonKind;
}

export interface Suspension {
  id: string;
  team: TeamSide;
  playerNo: string;
  playerName: string;
  who: PersonKind;
  durationSec: number;
  /** Match elapsed second when the suspension started. */
  startedAtElapsed: number;
  /** Match elapsed second when this suspension ends. */
  endsAtElapsed: number;
  /** Extra side clock. It is shown, and it does not use a 2′ slot. */
  manual?: boolean;
}

export type ScoreEventKind = 'goal' | 'half' | 'card' | 'suspension' | 'timeout' | 'period';

export interface ScoreEvent {
  id: string;
  atElapsed: number;
  periodName: string;
  team: TeamSide | 'ref';
  kind: ScoreEventKind;
  playerNo: string;
  playerName: string;
  detail: string;
}

export interface ScoreboardState {
  eventName: string;
  teamAName: string;
  teamBName: string;
  teamAColor: string;
  teamBColor: string;
  periods: ScoreboardPeriod[];
  periodIndex: number;
  elapsedSec: number;
  clockRunning: boolean;
  clockStartedAt: number | null;
  /** Break between periods. While visible, the venue board shows this countdown instead of the match clock. */
  breakDurationSec: number;
  breakElapsedSec: number;
  breakRunning: boolean;
  breakStartedAt: number | null;
  breakVisible: boolean;
  /** Half-time horn. `min3` sounds at 3:00 left. `custom` uses `breakBeepSec`. */
  breakBeep: BreakBeep;
  breakBeepSec: number;
  /** Legacy flag. Count-up is the default; `clockMode` wins when set. */
  countdown: boolean;
  clockMode?: 'up' | 'down' | 'custom';
  timeoutKind: TimeoutKind;
  timeoutElapsedSec: number;
  timeoutStartedAt: number | null;
  timeoutsPerTeam: number;
  /** Which T1/T2/T3 badges are lit on the venue board. Independent of the timeout clock. */
  timeoutShownA: boolean[];
  timeoutShownB: boolean[];
  /** T slots skipped for the match, shown dim and no longer available. */
  timeoutDimA?: boolean[];
  timeoutDimB?: boolean[];
  displayLang: DisplayLang;
  cards: PenaltyCard[];
  suspensions: Suspension[];
  events: ScoreEvent[];
  /** A team goal is on the board and still needs the scoring player. */
  pendingScore?: { team: TeamSide; delta: 1 | -1 } | null;
  playersA: ScoreboardPlayer[];
  playersB: ScoreboardPlayer[];
  officialsA: ScoreboardOfficial[];
  officialsB: ScoreboardOfficial[];
  sevenAIn: number;
  sevenAOut: number;
  sevenBIn: number;
  sevenBOut: number;
  /** Extra clock shown under the match clock. `time` counts a duration, `datetime` counts down to `deputyEndsAt`, `text` shows `deputyText`. */
  deputyMode?: 'time' | 'datetime' | 'text';
  deputyText?: string;
  deputyDurationSec?: number;
  deputyElapsedSec?: number;
  /** Absolute end time for `datetime` mode. */
  deputyEndsAt?: number | null;
  deputyRunning?: boolean;
  deputyStartedAt?: number | null;
  deputyVisible?: boolean;
  /** Venue-board look. Missing parts stay visible with the default colours. */
  display?: DisplaySettings;
}

export interface DisplayPart {
  visible: boolean;
  color: string;
  scale: number;
}

export interface DisplaySettings {
  event: DisplayPart;
  team: DisplayPart;
  score: DisplayPart;
  periodName: DisplayPart;
  periodScores: DisplayPart;
  clock: DisplayPart;
  timeout: DisplayPart;
  suspension: DisplayPart;
  cards: DisplayPart;
  players: DisplayPart;
}

export const DISPLAY_KEYS = [
  'event',
  'team',
  'score',
  'periodName',
  'periodScores',
  'clock',
  'timeout',
  'suspension',
  'cards',
  'players',
] as const;

export type DisplayKey = (typeof DISPLAY_KEYS)[number];

export function defaultDisplay(): DisplaySettings {
  const part = (color: string): DisplayPart => ({ visible: true, color, scale: 1 });
  return {
    event: part('#ff2a2a'),
    team: part('#ff2a2a'),
    score: part('#ffee00'),
    periodName: part('#ff2a2a'),
    periodScores: part('#ffee00'),
    clock: part('#ffffff'),
    timeout: part('#ffffff'),
    suspension: part('#ffb020'),
    cards: part('#ffffff'),
    players: part('#ffe14a'),
  };
}

export function normalizeDisplay(raw?: Partial<DisplaySettings> | null): DisplaySettings {
  const base = defaultDisplay();
  if (!raw) return base;
  const next = { ...base };
  for (const key of DISPLAY_KEYS) {
    const part = raw[key];
    if (!part) continue;
    const scale = Number(part.scale);
    next[key] = {
      visible: part.visible !== false,
      color: part.color || base[key].color,
      scale: Number.isFinite(scale) ? Math.min(2, Math.max(0.6, scale)) : 1,
    };
  }
  return next;
}

export function setDisplayPart(
  state: ScoreboardState,
  key: DisplayKey,
  patch: Partial<DisplayPart>
): ScoreboardState {
  const display = normalizeDisplay(state.display);
  return { ...state, display: { ...display, [key]: { ...display[key], ...patch } } };
}

export interface ScoreboardMatch {
  id: string;
  title: string;
  home_team: string | null;
  away_team: string | null;
  state: ScoreboardState;
  created_at: string;
  updated_at: string;
}

const CARD_COLORS: Record<CardKind, string> = {
  yellow: '#f5d90a',
  red: '#e53935',
  blue: '#1e88e5',
};

export function cardColor(kind: CardKind): string {
  return CARD_COLORS[kind];
}

export function cardLabel(kind: CardKind): string {
  if (kind === 'yellow') return 'W';
  if (kind === 'red') return 'D';
  return 'DR';
}

export function cardTitle(kind: CardKind): string {
  if (kind === 'yellow') return '警告 W';
  if (kind === 'red') return '取消資格 D';
  return '報告取消資格 DR';
}

function nid(): string {
  return crypto.randomUUID();
}

const PERIOD_DISPLAY_ZH: Record<string, string> = {
  決勝期1上: '加時1上',
  決勝期1下: '加時1下',
  決勝期2上: '加時2上',
  決勝期2下: '加時2下',
  決勝期: '加時',
};

const PERIOD_DISPLAY_EN: Record<string, string> = {
  上半場: '1st Half',
  下半場: '2nd Half',
  上半時: '1st Half',
  下半時: '2nd Half',
  決勝期1上: 'OT1 1st',
  決勝期1下: 'OT1 2nd',
  決勝期2上: 'OT2 1st',
  決勝期2下: 'OT2 2nd',
  加時1上: 'OT1 1st',
  加時1下: 'OT1 2nd',
  加時2上: 'OT2 1st',
  加時2下: 'OT2 2nd',
  決勝期: 'Overtime',
  加時: 'Overtime',
  七米球: '7-metre',
};

/** Short set name for the venue board and the control desk. */
export function periodDisplayName(name: string, lang: 'zh' | 'en' = 'zh'): string {
  const table = lang === 'en' ? PERIOD_DISPLAY_EN : PERIOD_DISPLAY_ZH;
  return table[name] || name;
}

export function defaultPeriods(halfMinutes = 30): ScoreboardPeriod[] {
  const half = Math.max(1, halfMinutes) * 60;
  const defs: Array<[string, number]> = [
    ['上半場', half],
    ['下半場', half],
    ['加時1上', 300],
    ['加時1下', 300],
    ['加時2上', 300],
    ['加時2下', 300],
    ['七米球', 0],
  ];
  return defs.map(([name, durationSec]) => ({
    id: nid(),
    name,
    durationSec,
    goalsA: 0,
    halfA: 0,
    goalsB: 0,
    halfB: 0,
    timeoutsUsedA: 0,
    timeoutsUsedB: 0,
  }));
}

export function createScoreboardState(input: {
  eventName?: string;
  teamAName?: string;
  teamBName?: string;
  halfMinutes?: number;
  timeoutsPerTeam?: number;
}): ScoreboardState {
  return {
    eventName: input.eventName?.trim() || '手球賽事',
    teamAName: input.teamAName?.trim() || 'A隊',
    teamBName: input.teamBName?.trim() || 'B隊',
    teamAColor: '#1565c0',
    teamBColor: '#c62828',
    periods: defaultPeriods(input.halfMinutes ?? 30),
    periodIndex: 0,
    elapsedSec: 0,
    clockRunning: false,
    clockStartedAt: null,
    breakDurationSec: 600,
    breakElapsedSec: 0,
    breakRunning: false,
    breakStartedAt: null,
    breakVisible: false,
    breakBeep: 'off',
    breakBeepSec: 180,
    countdown: false,
    clockMode: 'up',
    timeoutKind: 'none',
    timeoutElapsedSec: 0,
    timeoutStartedAt: null,
    timeoutsPerTeam: input.timeoutsPerTeam ?? 3,
    timeoutShownA: [],
    timeoutShownB: [],
    timeoutDimA: [],
    timeoutDimB: [],
    displayLang: 'zh',
    cards: [],
    suspensions: [],
    events: [],
    pendingScore: null,
    playersA: [],
    playersB: [],
    officialsA: [],
    officialsB: [],
    sevenAIn: 0,
    sevenAOut: 0,
    sevenBIn: 0,
    sevenBOut: 0,
    deputyMode: 'time',
    deputyText: '',
    deputyDurationSec: 0,
    deputyElapsedSec: 0,
    deputyEndsAt: null,
    deputyRunning: false,
    deputyStartedAt: null,
    deputyVisible: false,
    display: defaultDisplay(),
  };
}

export function deputyRemaining(state: ScoreboardState, now = Date.now()): number {
  if (state.deputyMode === 'text') return 0;
  if (state.deputyMode === 'datetime') {
    const ends = state.deputyEndsAt || 0;
    return Math.max(0, Math.ceil((ends - now) / 1000));
  }
  const duration = state.deputyDurationSec || 0;
  let elapsed = state.deputyElapsedSec || 0;
  if (state.deputyRunning && state.deputyStartedAt != null) {
    elapsed += Math.max(0, Math.floor((now - state.deputyStartedAt) / 1000));
  }
  return Math.max(0, duration - elapsed);
}

export function setDeputyDuration(state: ScoreboardState, seconds: number): ScoreboardState {
  return {
    ...state,
    deputyMode: 'time',
    deputyDurationSec: Math.max(0, Math.floor(seconds)),
    deputyElapsedSec: 0,
    deputyRunning: false,
    deputyStartedAt: null,
  };
}

export function setDeputyTarget(state: ScoreboardState, endsAt: number): ScoreboardState {
  return {
    ...state,
    deputyMode: 'datetime',
    deputyEndsAt: endsAt,
    deputyText: '',
    deputyDurationSec: 0,
    deputyElapsedSec: 0,
    deputyRunning: false,
    deputyStartedAt: null,
  };
}

export function startDeputy(state: ScoreboardState, now = Date.now()): ScoreboardState {
  if (state.breakVisible) return state;
  const paused = freezeClock(
    {
      ...state,
      timeoutKind: 'none',
      timeoutElapsedSec: 0,
      timeoutStartedAt: null,
    },
    now
  );
  if (paused.deputyMode === 'text') {
    return { ...paused, deputyVisible: true, deputyRunning: false, deputyStartedAt: null };
  }
  const remain = deputyRemaining(paused, now);
  if (paused.deputyMode === 'datetime') {
    return {
      ...paused,
      deputyVisible: remain > 0,
      deputyRunning: remain > 0,
      deputyStartedAt: remain > 0 ? now : null,
    };
  }
  return {
    ...paused,
    deputyVisible: true,
    deputyRunning: remain > 0,
    deputyStartedAt: remain > 0 ? now : null,
    deputyElapsedSec: Math.max(0, (paused.deputyDurationSec || 0) - remain),
  };
}

export function stopDeputy(state: ScoreboardState, now = Date.now()): ScoreboardState {
  if (state.deputyMode === 'datetime' || state.deputyMode === 'text') {
    return { ...state, deputyVisible: false, deputyRunning: false, deputyStartedAt: null };
  }
  const remain = deputyRemaining(state, now);
  return {
    ...state,
    deputyVisible: false,
    deputyRunning: false,
    deputyStartedAt: null,
    deputyElapsedSec: Math.max(0, (state.deputyDurationSec || 0) - remain),
  };
}

export function matchTitle(state: ScoreboardState): string {
  const event = state.eventName.trim();
  const home = state.teamAName.trim();
  const away = state.teamBName.trim();
  if (home && away) return event ? `${event} · ${home} vs ${away}` : `${home} vs ${away}`;
  return event || '比分牌';
}

export function formatClock(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export function formatLongClock(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  if (h <= 0) return formatClock(s);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export function formatScore(goals: number): string {
  return String(Math.max(0, Math.floor(goals)));
}

export function liveElapsed(state: ScoreboardState, now = Date.now()): number {
  if (!state.clockRunning || state.clockStartedAt == null) return state.elapsedSec;
  return state.elapsedSec + Math.max(0, Math.floor((now - state.clockStartedAt) / 1000));
}

export function periodOf(state: ScoreboardState): ScoreboardPeriod {
  return state.periods[state.periodIndex] ?? state.periods[0];
}

/** Match clock runs upward unless the operator explicitly chose countdown. */
export function isCountdown(state: ScoreboardState): boolean {
  return state.clockMode === 'down';
}

export function isCustomClock(state: ScoreboardState): boolean {
  return state.clockMode === 'custom';
}

function periodCap(state: ScoreboardState): number {
  if (isCustomClock(state)) return 0;
  return periodOf(state).durationSec;
}

export function clockSeconds(state: ScoreboardState, now = Date.now()): number {
  const elapsed = liveElapsed(state, now);
  const cap = periodCap(state);
  if (cap <= 0) return elapsed;
  const held = Math.min(elapsed, cap);
  if (isCountdown(state)) return Math.max(0, cap - held);
  return held;
}

export function periodTimeUp(state: ScoreboardState, now = Date.now()): boolean {
  const cap = periodCap(state);
  return cap > 0 && liveElapsed(state, now) >= cap;
}

function clearBreak(state: ScoreboardState): ScoreboardState {
  return {
    ...state,
    breakVisible: false,
    breakRunning: false,
    breakStartedAt: null,
    breakElapsedSec: 0,
  };
}

function breakElapsed(state: ScoreboardState, now = Date.now()): number {
  if (!state.breakRunning || state.breakStartedAt == null) return state.breakElapsedSec;
  return state.breakElapsedSec + Math.max(0, Math.floor((now - state.breakStartedAt) / 1000));
}

export function breakRemaining(state: ScoreboardState, now = Date.now()): number {
  return Math.max(0, (state.breakDurationSec || 0) - breakElapsed(state, now));
}

export function setBreakDuration(state: ScoreboardState, seconds: number): ScoreboardState {
  if (state.breakRunning) return state;
  return { ...state, breakDurationSec: Math.max(60, Math.floor(seconds)) };
}

export function setBreakRemaining(state: ScoreboardState, seconds: number, now = Date.now()): ScoreboardState {
  if (!state.breakVisible) return state;
  const remain = Math.max(0, Math.floor(seconds));
  const duration = Math.max(state.breakDurationSec, remain);
  return {
    ...state,
    breakDurationSec: duration,
    breakElapsedSec: duration - remain,
    breakStartedAt: state.breakRunning ? now : null,
  };
}

export function startHalfTime(state: ScoreboardState, now = Date.now()): ScoreboardState {
  const paused = stopDeputy(freezeClock(state, now), now);
  if (!periodTimeUp(paused, now) || paused.breakDurationSec <= 0) return paused;
  return {
    ...paused,
    timeoutKind: 'none',
    timeoutElapsedSec: 0,
    timeoutStartedAt: null,
    breakVisible: true,
    breakRunning: true,
    breakElapsedSec: 0,
    breakStartedAt: now,
  };
}

export function pauseHalfTime(state: ScoreboardState, now = Date.now()): ScoreboardState {
  if (!state.breakRunning) return state;
  const elapsed = Math.min(breakElapsed(state, now), state.breakDurationSec);
  return { ...state, breakElapsedSec: elapsed, breakRunning: false, breakStartedAt: null };
}

export function resumeHalfTime(state: ScoreboardState, now = Date.now()): ScoreboardState {
  if (!state.breakVisible || state.breakRunning || breakRemaining(state, now) <= 0) return state;
  return { ...state, breakRunning: true, breakStartedAt: now };
}

export function endHalfTime(state: ScoreboardState, now = Date.now()): ScoreboardState {
  const ended = clearBreak(pauseHalfTime(state, now));
  const nextIndex = ended.periodIndex + 1;
  if (nextIndex >= ended.periods.length) return ended;
  return selectPeriod(ended, nextIndex, now);
}

export function setClockMode(state: ScoreboardState, mode: 'up' | 'down' | 'custom'): ScoreboardState {
  if (state.clockRunning || state.breakVisible) return state;
  const shown = clockSeconds(state);
  const cap = periodOf(state).durationSec;
  const elapsed = mode === 'down' && cap > 0 ? Math.max(0, cap - shown) : shown;
  return { ...state, elapsedSec: elapsed, clockMode: mode, countdown: mode === 'down' };
}

export function timeoutSeconds(state: ScoreboardState, now = Date.now()): number {
  if (state.timeoutKind === 'none') return 0;
  const extra =
    state.timeoutStartedAt == null
      ? 0
      : Math.max(0, Math.floor((now - state.timeoutStartedAt) / 1000));
  return state.timeoutElapsedSec + extra;
}

export function periodScore(state: ScoreboardState, team: TeamSide): { goals: number; halves: number } {
  const period = periodOf(state);
  if (team === 'A') return { goals: period.goalsA, halves: period.halfA };
  return { goals: period.goalsB, halves: period.halfB };
}

export function playerPoints(player: ScoreboardPlayer): number {
  return player.goals || 0;
}

export function teamScore(state: ScoreboardState, team: TeamSide): { goals: number; halves: number } {
  return state.periods.reduce(
    (acc, p) => {
      if (team === 'A') {
        acc.goals += p.goalsA;
        acc.halves += p.halfA;
      } else {
        acc.goals += p.goalsB;
        acc.halves += p.halfB;
      }
      return acc;
    },
    { goals: 0, halves: 0 }
  );
}

export function activeSuspensions(state: ScoreboardState, now = Date.now()): Array<Suspension & { remainingSec: number }> {
  const elapsed = liveElapsed(state, now);
  return state.suspensions
    .map((s) => ({ ...s, remainingSec: Math.max(0, s.endsAtElapsed - elapsed) }))
    .filter((s) => s.remainingSec > 0);
}

function freezeClock(state: ScoreboardState, now = Date.now()): ScoreboardState {
  const cap = periodCap(state);
  const raw = state.clockRunning ? liveElapsed(state, now) : state.elapsedSec;
  const elapsedSec = cap > 0 ? Math.min(raw, cap) : raw;
  if (!state.clockRunning && elapsedSec === state.elapsedSec) return state;
  return {
    ...state,
    elapsedSec,
    clockRunning: false,
    clockStartedAt: null,
  };
}

function freezeTimeout(state: ScoreboardState, now = Date.now()): ScoreboardState {
  if (state.timeoutKind === 'none') return state;
  return {
    ...state,
    timeoutElapsedSec: timeoutSeconds(state, now),
    timeoutStartedAt: null,
  };
}

export function startClock(state: ScoreboardState, now = Date.now()): ScoreboardState {
  const cleared = stopDeputy(
    { ...freezeTimeout(state, now), timeoutKind: 'none' as TimeoutKind, timeoutElapsedSec: 0, timeoutStartedAt: null },
    now
  );
  if (cleared.clockRunning) return cleared;
  if (periodTimeUp(cleared, now)) return cleared;
  return { ...clearBreak(cleared), clockRunning: true, clockStartedAt: now };
}

export function pauseClock(state: ScoreboardState, now = Date.now()): ScoreboardState {
  return freezeClock(state, now);
}

export function finishTeamTimeout(state: ScoreboardState): ScoreboardState {
  if (state.timeoutKind !== 'A' && state.timeoutKind !== 'B') return state;
  return { ...state, timeoutKind: 'none', timeoutElapsedSec: 0, timeoutStartedAt: null };
}

export function breakBeepMark(state: ScoreboardState): number | null {
  if (state.breakBeep === 'min3') return 180;
  if (state.breakBeep === 'custom') return Math.max(0, Math.floor(state.breakBeepSec || 0));
  return null;
}

function secondHalfLastFive(state: ScoreboardState, now = Date.now()): boolean {
  const period = periodOf(state);
  const isSecond = state.periodIndex === 1 || period.name === '下半場' || period.name === '下半時';
  if (!isSecond || isCustomClock(state)) return false;
  const cap = period.durationSec;
  if (cap <= 0) return false;
  return Math.max(0, cap - liveElapsed(state, now)) <= 300;
}

/** Which T badge a new team timeout should light. `null` means no timeout is allowed now. */
export function chooseTimeoutSlot(state: ScoreboardState, team: TeamSide, now = Date.now()): number | null {
  const flags = timeoutShown(state, team);
  const dim = timeoutDimmed(state, team);
  const free = (index: number) => index < flags.length && !flags[index] && !dim[index];
  const period = periodOf(state);
  const isFirst = state.periodIndex === 0 || period.name === '上半場' || period.name === '上半時';
  const isSecond = state.periodIndex === 1 || period.name === '下半場' || period.name === '下半時';
  const lastFive = secondHalfLastFive(state, now);

  if (isSecond) {
    if (lastFive && free(2) && !flags[1]) return 2;
    if (free(1)) return 1;
    if (free(2)) return 2;
    return null;
  }
  if (isFirst) {
    if (free(0)) return 0;
    if (free(1)) return 1;
    return null;
  }
  for (let index = 0; index < flags.length; index += 1) {
    if (free(index)) return index;
  }
  return null;
}

export function setTimeoutRemaining(state: ScoreboardState, seconds: number, now = Date.now()): ScoreboardState {
  if (state.timeoutKind !== 'A' && state.timeoutKind !== 'B') return state;
  const remain = Math.max(0, Math.min(60, Math.floor(seconds)));
  return {
    ...state,
    timeoutElapsedSec: 60 - remain,
    timeoutStartedAt: state.timeoutStartedAt != null ? now : state.timeoutStartedAt,
  };
}

export function startTimeout(state: ScoreboardState, kind: Exclude<TimeoutKind, 'none'>, now = Date.now()): ScoreboardState {
  if (state.timeoutKind === kind) return state;
  if (state.breakVisible) return state;
  const idle = stopDeputy(
    { ...state, timeoutKind: 'none' as TimeoutKind, timeoutElapsedSec: 0, timeoutStartedAt: null },
    now
  );
  const paused = freezeClock(idle, now);
  const slot = kind === 'A' || kind === 'B' ? chooseTimeoutSlot(paused, kind, now) : null;
  if ((kind === 'A' || kind === 'B') && slot == null) return state;
  const periods = paused.periods.map((p, i) => {
    if (i !== paused.periodIndex) return p;
    if (kind === 'A') return { ...p, timeoutsUsedA: p.timeoutsUsedA + 1 };
    if (kind === 'B') return { ...p, timeoutsUsedB: p.timeoutsUsedB + 1 };
    return p;
  });
  let shown = paused;
  if ((kind === 'A' || kind === 'B') && slot != null) {
    const flags = timeoutShown(paused, kind);
    const dim = timeoutDimmed(paused, kind);
    const skipT2 = slot === 2 && !flags[1] && secondHalfLastFive(paused, now);
    flags[slot] = true;
    if (skipT2) dim[1] = true;
    shown = kind === 'A'
      ? { ...paused, timeoutShownA: flags, timeoutDimA: dim }
      : { ...paused, timeoutShownB: flags, timeoutDimB: dim };
  }
  const next: ScoreboardState = {
    ...shown,
    periods,
    timeoutKind: kind,
    timeoutElapsedSec: 0,
    timeoutStartedAt: now,
  };
  const detail = kind === 'ref' ? '裁判暫停' : kind === 'A' ? 'A隊暫停' : 'B隊暫停';
  return withEvents(next, {
    team: kind === 'ref' ? 'ref' : kind,
    kind: 'timeout',
    playerNo: '',
    playerName: '',
    detail,
  });
}

export function timeoutShown(state: ScoreboardState, team: TeamSide): boolean[] {
  const raw = team === 'A' ? state.timeoutShownA : state.timeoutShownB;
  return Array.from({ length: state.timeoutsPerTeam }, (_, i) => Boolean(raw?.[i]));
}

export function timeoutDimmed(state: ScoreboardState, team: TeamSide): boolean[] {
  const raw = team === 'A' ? state.timeoutDimA : state.timeoutDimB;
  return Array.from({ length: state.timeoutsPerTeam }, (_, i) => Boolean(raw?.[i]));
}

export function toggleTimeoutShown(state: ScoreboardState, team: TeamSide, index: number): ScoreboardState {
  if (index < 0 || index >= state.timeoutsPerTeam) return state;
  const flags = timeoutShown(state, team);
  const dim = timeoutDimmed(state, team);
  flags[index] = !flags[index];
  if (flags[index]) dim[index] = false;
  return team === 'A'
    ? { ...state, timeoutShownA: flags, timeoutDimA: dim }
    : { ...state, timeoutShownB: flags, timeoutDimB: dim };
}

function playersOf(state: ScoreboardState, team: TeamSide): ScoreboardPlayer[] {
  return team === 'A' ? state.playersA : state.playersB;
}

function playerByNo(state: ScoreboardState, team: TeamSide, playerNo: string): ScoreboardPlayer | undefined {
  const no = playerNo.trim();
  if (!no) return undefined;
  return playersOf(state, team).find((p) => p.number.trim() === no);
}

function withEvents(state: ScoreboardState, event: Omit<ScoreEvent, 'id' | 'atElapsed' | 'periodName'> & { atElapsed?: number }): ScoreboardState {
  const row: ScoreEvent = {
    id: nid(),
    atElapsed: event.atElapsed ?? liveElapsed(state),
    periodName: periodOf(state).name,
    team: event.team,
    kind: event.kind,
    playerNo: event.playerNo,
    playerName: event.playerName,
    detail: event.detail,
  };
  return { ...state, events: [...(state.events || []), row] };
}

export function adjustScore(
  state: ScoreboardState,
  team: TeamSide,
  delta: number,
  player?: { number?: string; name?: string }
): ScoreboardState {
  const current = periodScore(state, team).goals;
  if (current + delta < 0) return state;
  const next: ScoreboardState = {
    ...state,
    periods: state.periods.map((p, i) => {
      if (i !== state.periodIndex) return p;
      if (team === 'A') return { ...p, goalsA: p.goalsA + delta };
      return { ...p, goalsB: p.goalsB + delta };
    }),
  };
  const sign = delta > 0 ? '+' : '';
  return withEvents(next, {
    team,
    kind: 'goal',
    playerNo: player?.number?.trim() || '',
    playerName: player?.name?.trim() || '',
    detail: `${sign}${delta}`,
  });
}

function shiftPeriodGoals(state: ScoreboardState, team: TeamSide, delta: number): ScoreboardState {
  const current = periodScore(state, team).goals;
  if (current + delta < 0) return state;
  return {
    ...state,
    periods: state.periods.map((p, i) => {
      if (i !== state.periodIndex) return p;
      if (team === 'A') return { ...p, goalsA: p.goalsA + delta };
      return { ...p, goalsB: p.goalsB + delta };
    }),
  };
}

/** Adds or removes a team goal and waits for the operator to pick the player. */
export function beginTeamScore(state: ScoreboardState, team: TeamSide, delta: 1 | -1): ScoreboardState {
  if (state.pendingScore) return state;
  if (delta < 0 && !playersOf(state, team).some((p) => (p.goals || 0) > 0)) return state;
  const next = shiftPeriodGoals(state, team, delta);
  if (next === state) return state;
  return withEvents(
    { ...next, pendingScore: { team, delta } },
    {
      team,
      kind: 'goal',
      playerNo: '',
      playerName: '',
      detail: delta > 0 ? '+1' : '-1',
    }
  );
}

/** Attaches the pending team goal to one player. */
export function assignScorer(state: ScoreboardState, playerId: string): ScoreboardState {
  const pending = state.pendingScore;
  if (!pending) return state;
  const player = playersOf(state, pending.team).find((p) => p.id === playerId);
  if (!player) return state;
  const goals = (player.goals || 0) + pending.delta;
  if (goals < 0) return state;
  const patched = updatePlayer(state, pending.team, playerId, { goals });
  const events = [...patched.events];
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const event = events[i];
    if (event.kind === 'goal' && event.team === pending.team && !event.playerNo) {
      events[i] = { ...event, playerNo: player.number.trim() || '—', playerName: player.name.trim() };
      break;
    }
  }
  return { ...patched, pendingScore: null, events };
}

/** Drops an unassigned team goal and puts the score back. */
export function cancelPendingScore(state: ScoreboardState): ScoreboardState {
  const pending = state.pendingScore;
  if (!pending) return state;
  const next = shiftPeriodGoals(state, pending.team, -pending.delta);
  const events = [...next.events];
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const event = events[i];
    if (event.kind === 'goal' && event.team === pending.team && !event.playerNo) {
      events.splice(i, 1);
      break;
    }
  }
  return { ...next, pendingScore: null, events };
}

/** Changes one player's goal count without changing the team score. */
export function adjustPlayerGoals(state: ScoreboardState, team: TeamSide, playerId: string, delta: number): ScoreboardState {
  const player = playersOf(state, team).find((p) => p.id === playerId);
  if (!player) return state;
  const goals = (player.goals || 0) + delta;
  if (goals < 0) return state;
  if (delta > 0) {
    const teamGoals = teamScore(state, team).goals;
    const playerGoals = playersOf(state, team).reduce((sum, row) => sum + (row.goals || 0), 0);
    if (playerGoals >= teamGoals) return state;
  }
  return updatePlayer(state, team, playerId, { goals });
}

export function adjustPlayerScore(state: ScoreboardState, team: TeamSide, playerId: string, delta: number): ScoreboardState {
  const player = playersOf(state, team).find((p) => p.id === playerId);
  if (!player) return state;
  if (player.goals + delta < 0) return state;
  const patched = updatePlayer(state, team, playerId, { goals: player.goals + delta });
  return adjustScore(patched, team, delta, player);
}

export function selectPeriod(state: ScoreboardState, index: number, now = Date.now()): ScoreboardState {
  const paused = freezeClock(freezeTimeout({ ...state, timeoutKind: 'none', timeoutElapsedSec: 0, timeoutStartedAt: null }, now), now);
  if (index < 0 || index >= paused.periods.length) return paused;
  const fromElapsed = paused.elapsedSec;
  const kept = {
    ...paused,
    suspensions: paused.suspensions.map((s) => ({
      ...s,
      startedAtElapsed: s.startedAtElapsed - fromElapsed,
      endsAtElapsed: s.endsAtElapsed - fromElapsed,
    })),
  };
  const next = { ...clearBreak(kept), periodIndex: index, elapsedSec: 0, clockRunning: false, clockStartedAt: null };
  const period = periodOf(next);
  return withEvents(next, {
    team: 'A',
    kind: 'period',
    playerNo: '',
    playerName: '',
    detail: period.name,
    atElapsed: 0,
  });
}

export function setElapsed(state: ScoreboardState, seconds: number): ScoreboardState {
  const paused = freezeClock(state);
  const cap = periodCap(paused);
  const elapsed = Math.max(0, Math.floor(seconds));
  return { ...paused, elapsedSec: cap > 0 ? Math.min(elapsed, cap) : elapsed };
}

export function teamWarningCount(state: ScoreboardState, team: TeamSide, who: PersonKind): number {
  return state.cards.filter((c) => c.team === team && c.who === who && c.kind === 'yellow').length;
}

export function teamOfficialTwoCount(state: ScoreboardState, team: TeamSide): number {
  return state.suspensions
    .filter((s) => s.team === team && s.who === 'official' && !s.manual)
    .reduce((sum, s) => sum + (s.durationSec >= 240 ? 2 : 1), 0);
}

/** Players: 1 W, 1 D, 1 DR, and 3 team warnings. Officials: 1 W, 1 D, 1 DR, and 1 team warning. */
export function canAddCard(state: ScoreboardState, team: TeamSide, who: PersonKind, playerNo: string, kind: CardKind): boolean {
  const no = playerNo.trim();
  if (!no) return false;
  const marks = penaltyMarks(state, team, who, no);
  if (kind === 'yellow') {
    const teamLimit = who === 'player' ? 3 : 1;
    return marks.w < 1 && teamWarningCount(state, team, who) < teamLimit;
  }
  if (kind === 'red') return marks.d < 1;
  return marks.dr < 1;
}

/** 4′ counts as two 2′. Players max 2. Each official max 1, and the team of officials max 1. */
export function canAddTwo(state: ScoreboardState, team: TeamSide, who: PersonKind, playerNo: string, units: 1 | 2): boolean {
  const no = playerNo.trim();
  if (!no) return false;
  const marks = penaltyMarks(state, team, who, no);
  if (who === 'player') return marks.two + units <= 2;
  if (units !== 1) return false;
  return marks.two < 1 && teamOfficialTwoCount(state, team) < 1;
}

export function addCard(
  state: ScoreboardState,
  team: TeamSide,
  kind: CardKind,
  playerNo: string,
  playerName = '',
  who: PersonKind = 'player',
  now = Date.now()
): ScoreboardState {
  const no = playerNo.trim();
  if (!canAddCard(state, team, who, no, kind)) return state;
  const known = who === 'player' ? playerByNo(state, team, no) : undefined;
  const name = playerName.trim() || known?.name || '';
  const next: ScoreboardState = {
    ...state,
    cards: [...state.cards, { id: nid(), team, kind, playerNo: no || name, playerName: name, who }],
  };
  const carded = withEvents(next, {
    team,
    kind: 'card',
    playerNo: no,
    playerName: name,
    detail: cardLabel(kind),
  });
  if (kind === 'yellow') return carded;
  return addSuspension(carded, team, no, 120, now, who, name, true);
}

export function removeLastCard(
  state: ScoreboardState,
  team: TeamSide,
  who: PersonKind,
  playerNo: string,
  kind: CardKind
): ScoreboardState {
  const no = playerNo.trim();
  const hit = [...state.cards].reverse().find((c) => c.team === team && c.who === who && c.kind === kind && c.playerNo === no);
  if (!hit) return state;
  return removeCard(state, hit.id);
}

export function removeCard(state: ScoreboardState, id: string): ScoreboardState {
  return { ...state, cards: state.cards.filter((c) => c.id !== id) };
}

export function addSuspension(
  state: ScoreboardState,
  team: TeamSide,
  playerNo: string,
  durationSec: number,
  now = Date.now(),
  who: PersonKind = 'player',
  playerName = '',
  forced = false
): ScoreboardState {
  const no = playerNo.trim();
  const units: 1 | 2 = durationSec >= 240 ? 2 : 1;
  if (!forced && !canAddTwo(state, team, who, no, units)) return state;
  const startedAtElapsed = liveElapsed(state, now);
  const known = who === 'player' ? playerByNo(state, team, no) : undefined;
  const name = playerName.trim() || known?.name || '';
  const playersKey = team === 'A' ? 'playersA' : 'playersB';
  const next: ScoreboardState = {
    ...state,
    [playersKey]:
      who === 'player'
        ? state[playersKey].map((p) => (p.number.trim() === no ? { ...p, twoMin: (p.twoMin || 0) + units } : p))
        : state[playersKey],
    suspensions: [
      ...state.suspensions,
      {
        id: nid(),
        team,
        playerNo: no,
        playerName: name,
        who,
        durationSec,
        startedAtElapsed,
        endsAtElapsed: startedAtElapsed + durationSec,
      },
    ],
  };
  return withEvents(next, {
    team,
    kind: 'suspension',
    playerNo: no,
    playerName: name,
    detail: durationSec >= 240 ? "4'" : "2'",
    atElapsed: startedAtElapsed,
  });
}

export function removeLastSuspension(state: ScoreboardState, team: TeamSide, who: PersonKind, playerNo: string): ScoreboardState {
  const no = playerNo.trim();
  const hit = [...state.suspensions].reverse().find((s) => s.team === team && s.who === who && s.playerNo === no);
  if (!hit) return state;
  const next = removeSuspension(state, hit.id);
  if (who !== 'player') return next;
  const units = hit.durationSec >= 240 ? 2 : 1;
  const playersKey = team === 'A' ? 'playersA' : 'playersB';
  return {
    ...next,
    [playersKey]: next[playersKey].map((p) =>
      p.number.trim() === no ? { ...p, twoMin: Math.max(0, (p.twoMin || 0) - units) } : p
    ),
  };
}

export interface PenaltyMarks {
  w: number;
  two: number;
  d: number;
  dr: number;
}

export function penaltyMarks(state: ScoreboardState, team: TeamSide, who: PersonKind, playerNo: string): PenaltyMarks {
  const no = playerNo.trim();
  const cards = state.cards.filter((c) => c.team === team && c.who === who && c.playerNo === no);
  const two = state.suspensions
    .filter((s) => s.team === team && s.who === who && s.playerNo === no && !s.manual)
    .reduce((sum, s) => sum + (s.durationSec >= 240 ? 2 : 1), 0);
  return {
    w: cards.filter((c) => c.kind === 'yellow').length,
    two,
    d: cards.filter((c) => c.kind === 'red').length,
    dr: cards.filter((c) => c.kind === 'blue').length,
  };
}

export function timeoutsUsed(state: ScoreboardState, team: TeamSide): number {
  const period = periodOf(state);
  return team === 'A' ? period.timeoutsUsedA : period.timeoutsUsedB;
}

function rosterPlayer(state: ScoreboardState, team: TeamSide, playerNo: string): ScoreboardPlayer | undefined {
  const no = playerNo.trim();
  return playersOf(state, team).find((p) => p.number.trim() === no);
}

export function moveSubclock(state: ScoreboardState, id: string, playerNo: string): { state: ScoreboardState; error?: string } {
  const current = state.suspensions.find((s) => s.id === id);
  if (!current || current.who === 'official') return { state, error: '找不到副時鐘' };
  const to: TeamSide = current.team === 'A' ? 'B' : 'A';
  const player = rosterPlayer(state, to, playerNo);
  if (!player) return { state, error: '對方沒有此號碼' };
  const units: 1 | 2 = current.durationSec >= 240 ? 2 : 1;
  if (!current.manual && !canAddTwo(state, to, 'player', player.number, units)) {
    return { state, error: '對方此號碼的 2′ 已達上限' };
  }
  let next: ScoreboardState = {
    ...state,
    suspensions: state.suspensions.map((s) =>
      s.id === id ? { ...s, team: to, playerNo: player.number.trim(), playerName: player.name } : s
    ),
  };
  if (!current.manual) {
    const take = (team: TeamSide, no: string, delta: number) => {
      const key = team === 'A' ? 'playersA' : 'playersB';
      next = {
        ...next,
        [key]: next[key].map((p) =>
          p.number.trim() === no ? { ...p, twoMin: Math.max(0, (p.twoMin || 0) + delta) } : p
        ),
      };
    };
    take(current.team, current.playerNo, -units);
    take(to, player.number, units);
  }
  return { state: next };
}

export function addManualSubclock(
  state: ScoreboardState,
  team: TeamSide,
  playerNo: string,
  durationSec: number,
  now = Date.now()
): { state: ScoreboardState; error?: string } {
  const player = rosterPlayer(state, team, playerNo);
  if (!player) return { state, error: '此隊沒有此號碼' };
  const seconds = Math.floor(durationSec);
  if (seconds <= 0) return { state, error: '請輸入時間' };
  const startedAtElapsed = liveElapsed(state, now);
  return {
    state: {
      ...state,
      suspensions: [
        ...state.suspensions,
        {
          id: nid(),
          team,
          playerNo: player.number.trim(),
          playerName: player.name,
          who: 'player',
          manual: true,
          durationSec: seconds,
          startedAtElapsed,
          endsAtElapsed: startedAtElapsed + seconds,
        },
      ],
    },
  };
}

export function updateSuspension(
  state: ScoreboardState,
  id: string,
  patch: { playerNo?: string; remainingSec?: number },
  now = Date.now()
): ScoreboardState {
  const current = state.suspensions.find((s) => s.id === id);
  if (!current) return state;
  const elapsed = liveElapsed(state, now);
  const playerNo = patch.playerNo == null ? current.playerNo : patch.playerNo.trim();
  const roster = current.who === 'player'
    ? (current.team === 'A' ? state.playersA : state.playersB)
    : (current.team === 'A' ? state.officialsA : state.officialsB);
  const known = roster.find((p) => p.number.trim() === playerNo);
  const remaining = patch.remainingSec == null
    ? Math.max(0, current.endsAtElapsed - elapsed)
    : Math.max(0, Math.floor(patch.remainingSec));
  return {
    ...state,
    suspensions: state.suspensions.map((s) => (
      s.id === id
        ? {
          ...s,
          playerNo,
          playerName: known?.name || (playerNo === current.playerNo ? current.playerName : ''),
          endsAtElapsed: elapsed + remaining,
        }
        : s
    )),
  };
}

export function removeSuspension(state: ScoreboardState, id: string): ScoreboardState {
  return { ...state, suspensions: state.suspensions.filter((s) => s.id !== id) };
}

export function pruneSuspensions(state: ScoreboardState, now = Date.now()): ScoreboardState {
  const elapsed = liveElapsed(state, now);
  const next = state.suspensions.filter((s) => s.endsAtElapsed > elapsed);
  if (next.length === state.suspensions.length) return state;
  return { ...state, suspensions: next };
}

function zeroScores(state: ScoreboardState): ScoreboardState {
  const fromElapsed = liveElapsed(state);
  return {
    ...state,
    periodIndex: 0,
    elapsedSec: 0,
    clockRunning: false,
    clockStartedAt: null,
    breakElapsedSec: 0,
    breakRunning: false,
    breakStartedAt: null,
    breakVisible: false,
    timeoutKind: 'none',
    timeoutElapsedSec: 0,
    timeoutStartedAt: null,
    pendingScore: null,
    periods: state.periods.map((p) => ({ ...p, goalsA: 0, goalsB: 0, halfA: 0, halfB: 0 })),
    playersA: state.playersA.map((p) => ({ ...p, goals: 0 })),
    playersB: state.playersB.map((p) => ({ ...p, goals: 0 })),
    suspensions: state.suspensions.map((s) => ({
      ...s,
      startedAtElapsed: s.startedAtElapsed - fromElapsed,
      endsAtElapsed: s.endsAtElapsed - fromElapsed,
    })),
  };
}

/** Clears the score and the match clock. Roster, cards, sub-clocks, and the event log stay. */
export function resetScoresAndClock(state: ScoreboardState): ScoreboardState {
  return zeroScores(state);
}

/** Clears the match record, including every player. Team names stay. */
export function resetMatch(state: ScoreboardState): ScoreboardState {
  const cleared = zeroScores(state);
  return {
    ...cleared,
    cards: [],
    suspensions: [],
    events: [],
    playersA: [],
    playersB: [],
    officialsA: [],
    officialsB: [],
    timeoutShownA: [],
    timeoutShownB: [],
    timeoutDimA: [],
    timeoutDimB: [],
    sevenAIn: 0,
    sevenAOut: 0,
    sevenBIn: 0,
    sevenBOut: 0,
    periods: cleared.periods.map((p) => ({ ...p, timeoutsUsedA: 0, timeoutsUsedB: 0 })),
  };
}

export function addPlayer(state: ScoreboardState, team: TeamSide): ScoreboardState {
  const row: ScoreboardPlayer = { id: nid(), number: '', name: '', goals: 0, half: 0, twoMin: 0 };
  if (team === 'A') return { ...state, playersA: [...state.playersA, row] };
  return { ...state, playersB: [...state.playersB, row] };
}

export function updatePlayer(
  state: ScoreboardState,
  team: TeamSide,
  id: string,
  patch: Partial<Pick<ScoreboardPlayer, 'number' | 'name' | 'goals' | 'half'>>
): ScoreboardState {
  const map = (list: ScoreboardPlayer[]) => list.map((p) => (p.id === id ? { ...p, ...patch } : p));
  if (team === 'A') return { ...state, playersA: map(state.playersA) };
  return { ...state, playersB: map(state.playersB) };
}

export function removePlayer(state: ScoreboardState, team: TeamSide, id: string): ScoreboardState {
  if (team === 'A') return { ...state, playersA: state.playersA.filter((p) => p.id !== id) };
  return { ...state, playersB: state.playersB.filter((p) => p.id !== id) };
}

function playerList(state: ScoreboardState, team: TeamSide): ScoreboardPlayer[] {
  return team === 'A' ? state.playersA : state.playersB;
}

function withPlayers(state: ScoreboardState, team: TeamSide, players: ScoreboardPlayer[]): ScoreboardState {
  return team === 'A' ? { ...state, playersA: players } : { ...state, playersB: players };
}

export function movePlayer(state: ScoreboardState, team: TeamSide, id: string, direction: -1 | 1): ScoreboardState {
  const list = [...playerList(state, team)];
  const index = list.findIndex((p) => p.id === id);
  const next = index + direction;
  if (index < 0 || next < 0 || next >= list.length) return state;
  const [row] = list.splice(index, 1);
  list.splice(next, 0, row);
  return withPlayers(state, team, list);
}

export function sortPlayersByNumber(state: ScoreboardState, team: TeamSide, order: 'asc' | 'desc'): ScoreboardState {
  const list = [...playerList(state, team)].sort((a, b) => {
    const an = a.number.trim();
    const bn = b.number.trim();
    const av = an === '' || Number.isNaN(Number(an)) ? Number.POSITIVE_INFINITY : Number(an);
    const bv = bn === '' || Number.isNaN(Number(bn)) ? Number.POSITIVE_INFINITY : Number(bn);
    if (av !== bv) return order === 'asc' ? av - bv : bv - av;
    return an.localeCompare(bn, 'zh-Hant', { numeric: true });
  });
  return withPlayers(state, team, list);
}

export function addOfficial(state: ScoreboardState, team: TeamSide): ScoreboardState {
  const row: ScoreboardOfficial = { id: nid(), number: '', name: '' };
  if (team === 'A') return { ...state, officialsA: [...state.officialsA, row] };
  return { ...state, officialsB: [...state.officialsB, row] };
}

export function updateOfficial(
  state: ScoreboardState,
  team: TeamSide,
  id: string,
  patch: Partial<Pick<ScoreboardOfficial, 'number' | 'name'>>
): ScoreboardState {
  const map = (list: ScoreboardOfficial[]) => list.map((p) => (p.id === id ? { ...p, ...patch } : p));
  if (team === 'A') return { ...state, officialsA: map(state.officialsA) };
  return { ...state, officialsB: map(state.officialsB) };
}

export function removeOfficial(state: ScoreboardState, team: TeamSide, id: string): ScoreboardState {
  if (team === 'A') return { ...state, officialsA: state.officialsA.filter((p) => p.id !== id) };
  return { ...state, officialsB: state.officialsB.filter((p) => p.id !== id) };
}

function officialList(state: ScoreboardState, team: TeamSide): ScoreboardOfficial[] {
  return team === 'A' ? state.officialsA : state.officialsB;
}

function withOfficials(state: ScoreboardState, team: TeamSide, officials: ScoreboardOfficial[]): ScoreboardState {
  return team === 'A' ? { ...state, officialsA: officials } : { ...state, officialsB: officials };
}

export function moveOfficial(state: ScoreboardState, team: TeamSide, id: string, direction: -1 | 1): ScoreboardState {
  const list = [...officialList(state, team)];
  const index = list.findIndex((p) => p.id === id);
  const next = index + direction;
  if (index < 0 || next < 0 || next >= list.length) return state;
  const [row] = list.splice(index, 1);
  list.splice(next, 0, row);
  return withOfficials(state, team, list);
}

export function sortOfficialsByNumber(state: ScoreboardState, team: TeamSide, order: 'asc' | 'desc'): ScoreboardState {
  const list = [...officialList(state, team)].sort((a, b) => {
    const an = a.number.trim();
    const bn = b.number.trim();
    const av = an === '' || Number.isNaN(Number(an)) ? Number.POSITIVE_INFINITY : Number(an);
    const bv = bn === '' || Number.isNaN(Number(bn)) ? Number.POSITIVE_INFINITY : Number(bn);
    if (av !== bv) return order === 'asc' ? av - bv : bv - av;
    return an.localeCompare(bn, 'zh-Hant', { numeric: true });
  });
  return withOfficials(state, team, list);
}

export function scoreboardChannel(matchId: string): string {
  return `hkhbref-scoreboard:${matchId}`;
}

function asPlayer(raw: Partial<ScoreboardPlayer> | undefined): ScoreboardPlayer {
  return {
    id: raw?.id || nid(),
    number: raw?.number || '',
    name: raw?.name || '',
    goals: raw?.goals || 0,
    half: raw?.half || 0,
    twoMin: raw?.twoMin || 0,
  };
}

function asSuspension(raw: Partial<Suspension>): Suspension {
  const ends = raw.endsAtElapsed || 0;
  const duration = raw.durationSec || 120;
  return {
    id: raw.id || nid(),
    team: raw.team === 'B' ? 'B' : 'A',
    playerNo: raw.playerNo || '',
    playerName: raw.playerName || '',
    who: raw.who === 'official' ? 'official' : 'player',
    manual: raw.manual === true,
    durationSec: duration,
    startedAtElapsed: raw.startedAtElapsed ?? Math.max(0, ends - duration),
    endsAtElapsed: ends,
  };
}

/** Fill fields missing from older saved matches and keep the clock counting up. */
export function normalizeState(raw: Partial<ScoreboardState> | null | undefined): ScoreboardState {
  const base = createScoreboardState({
    eventName: raw?.eventName,
    teamAName: raw?.teamAName,
    teamBName: raw?.teamBName,
  });
  if (!raw) return base;
  const clockMode = raw.clockMode === 'down' || raw.clockMode === 'custom' ? raw.clockMode : 'up';
  const slots = raw.timeoutsPerTeam ?? base.timeoutsPerTeam;
  const flags = (value: boolean[] | undefined) => Array.from({ length: slots }, (_, i) => Boolean(value?.[i]));
  return {
    ...base,
    ...raw,
    periods: raw.periods?.length ? raw.periods : base.periods,
    clockMode,
    countdown: clockMode === 'down',
    displayLang: raw.displayLang === 'en' ? 'en' : 'zh',
    breakDurationSec: raw.breakDurationSec && raw.breakDurationSec > 0 ? raw.breakDurationSec : 600,
    breakElapsedSec: raw.breakElapsedSec || 0,
    breakRunning: raw.breakRunning === true,
    breakStartedAt: raw.breakStartedAt ?? null,
    breakVisible: raw.breakVisible === true,
    breakBeep: raw.breakBeep === 'min3' || raw.breakBeep === 'custom' ? raw.breakBeep : 'off',
    breakBeepSec: raw.breakBeepSec && raw.breakBeepSec > 0 ? raw.breakBeepSec : 180,
    timeoutShownA: flags(raw.timeoutShownA),
    timeoutShownB: flags(raw.timeoutShownB),
    timeoutDimA: flags(raw.timeoutDimA),
    timeoutDimB: flags(raw.timeoutDimB),
    cards: (raw.cards || []).map((c) => ({
      id: c.id || nid(),
      team: c.team === 'B' ? 'B' as const : 'A' as const,
      kind: c.kind === 'red' || c.kind === 'blue' ? c.kind : 'yellow' as const,
      playerNo: c.playerNo || '',
      playerName: c.playerName || '',
      who: c.who === 'official' ? 'official' as const : 'player' as const,
    })),
    suspensions: (raw.suspensions || []).map((s) => asSuspension(s)),
    events: raw.events || [],
    pendingScore:
      raw.pendingScore &&
      (raw.pendingScore.team === 'A' || raw.pendingScore.team === 'B') &&
      (raw.pendingScore.delta === 1 || raw.pendingScore.delta === -1)
        ? { team: raw.pendingScore.team, delta: raw.pendingScore.delta }
        : null,
    playersA: (raw.playersA || []).map((p) => asPlayer(p)),
    playersB: (raw.playersB || []).map((p) => asPlayer(p)),
    officialsA: raw.officialsA || [],
    officialsB: raw.officialsB || [],
    display: normalizeDisplay(raw.display),
    deputyMode: raw.deputyMode === 'text' || raw.deputyMode === 'datetime' ? raw.deputyMode : 'time',
    deputyText: raw.deputyText || '',
    deputyDurationSec: raw.deputyDurationSec || 0,
    deputyElapsedSec: raw.deputyElapsedSec || 0,
    deputyEndsAt: raw.deputyEndsAt ?? null,
    deputyRunning: raw.deputyRunning === true,
    deputyStartedAt: raw.deputyStartedAt ?? null,
    deputyVisible: raw.deputyVisible === true,
  };
}

/** Lines like `12 陳大文` or `12,陳大文`. A header row is skipped. */
export function parseRosterText(text: string): Array<{ number: string; name: string }> {
  const rows: Array<{ number: string; name: string }> = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    if (/^(no\.?|號碼|号码|number)\b/i.test(trimmed)) continue;
    const match = trimmed.match(/^(\d+)\s*[,，\t]\s*(.*)$/) || trimmed.match(/^(\d+)\s+(.+)$/);
    if (!match) continue;
    rows.push({ number: match[1], name: match[2].trim() });
  }
  return rows;
}

export function importRoster(
  state: ScoreboardState,
  team: TeamSide,
  players: Array<{ number: string; name: string }>,
  officials: Array<{ number: string; name: string }>
): ScoreboardState {
  const asPlayers = players.map((row) => ({
    id: nid(),
    number: row.number,
    name: row.name,
    goals: 0,
    half: 0,
    twoMin: 0,
  }));
  const asOfficials = officials.map((row) => ({ id: nid(), number: row.number, name: row.name }));
  if (team === 'A') {
    return {
      ...state,
      playersA: players.length ? asPlayers : state.playersA,
      officialsA: officials.length ? asOfficials : state.officialsA,
    };
  }
  return {
    ...state,
    playersB: players.length ? asPlayers : state.playersB,
    officialsB: officials.length ? asOfficials : state.officialsB,
  };
}

export function eventLabel(event: ScoreEvent): string {
  if (event.kind === 'period') return `半場 ${event.detail}`;
  if (event.kind === 'timeout') return event.detail;
  const who = [event.playerNo, event.playerName].filter(Boolean).join(' ');
  return who ? `${who} ${event.detail}` : event.detail;
}
